import React, { useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import { useColorMode, Box, Progress, Spinner } from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import { 
    FaMapMarkerAlt, FaPhoneAlt, FaClock, FaPaperPlane, 
    FaArrowRight, FaArrowLeft, FaUser, FaEnvelope, 
    FaPhone, FaCheckCircle 
} from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";

const validateEmail = (email) => {
  return String(email).toLowerCase().match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
};

const validatePhone = (phone) => {
  const cleaned = phone.replace(/[\s.\-()+]/g, '');
  return /^(33|0)[0-9]{9}$/.test(cleaned);
};

const validateName = (name) => name.trim().length >= 2 && name.trim().length <= 60;

function Contact() {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

  const [formData, setFormData] = useState({ 
    nom: "", 
    prenom: "", 
    email: "", 
    telephone: "", 
    message: "" 
  });

  const [checkboxes, setCheckboxes] = useState({
    gardeEnfant: false, 
    aideHandicapes: false, 
    aideSeniors: false,
    transportPMR: false, 
    entretienDomicile: false, 
    aidesFinancieres: false,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setCheckboxes((prev) => ({ ...prev, [name]: checked }));
  };

  const nextStep = () => {
    const newErrors = {};
    if (!validateName(formData.nom)) newErrors.nom = "Minimum 2 caractères, max 60";
    if (!validateName(formData.prenom)) newErrors.prenom = "Minimum 2 caractères, max 60";
    if (!validateEmail(formData.email)) newErrors.email = "Adresse email invalide";
    if (!validatePhone(formData.telephone)) newErrors.telephone = "Format invalide (ex: 06 12 34 56 78)";
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) setStep((s) => s + 1);
  };
  const prevStep = () => setStep((s) => s - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (formData.message.trim().length < 10) {
      setErrors((prev) => ({ ...prev, message: "Message trop court (minimum 10 caractères)" }));
      return;
    }
    if (formData.message.trim().length > 2000) {
      setErrors((prev) => ({ ...prev, message: "Message trop long (maximum 2000 caractères)" }));
      return;
    }
    setLoading(true);

    const servicesSelectionnes = Object.entries(checkboxes)
      .filter(([_, checked]) => checked)
      .map(([key]) => key.replace(/([A-Z])/g, " $1"))
      .join(", ");

    const payload = { 
        ...formData, 
        services: servicesSelectionnes || "Aucun service sélectionné", 
        type: "CONTACT" 
    };

    try {
      const response = await axios.post(`${API_URL}/api/contact`, payload);
      toast.success(response.data.message || "Message envoyé avec succès !");
      setFormData({ nom: "", prenom: "", email: "", telephone: "", message: "" });
      setCheckboxes({ gardeEnfant: false, aideHandicapes: false, aideSeniors: false, transportPMR: false, entretienDomicile: false, aidesFinancieres: false });
      setStep(1);
    } catch (error) {
      toast.error("Impossible d’envoyer le message.");
    } finally {
      setLoading(false);
    }
  };

  const serviceOptions = [
    { id: "gardeEnfant", label: "Garde d'enfant" },
    { id: "aideHandicapes", label: "Aide Handicap" },
    { id: "aideSeniors", label: "Aide Seniors" },
    { id: "transportPMR", label: "Transport PMR" },
    { id: "entretienDomicile", label: "Entretien Domicile" },
    { id: "aidesFinancieres", label: "Aides Financières" },
  ];

  const inputStyle = `w-full px-6 py-5 rounded-[1.5rem] border outline-none transition-all duration-300 font-bold ${
    isDark 
    ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500" 
    : "bg-gray-50 border-gray-200 text-[#1B365D] placeholder-gray-500 focus:border-blue-600 focus:bg-white"
  }`;

  const labelStyle = "text-[10px] font-black uppercase tracking-[0.2em] mb-3 px-2 block text-blue-600";

  return (
    <Box className={isDark ? "bg-[#0a0f16] min-h-screen" : "bg-[#FAFCFF] min-h-screen"}>
      <Helmet>
        <title>Contact — Hetep Iaout Services</title>
        <meta name="description" content="Contactez Hetep Iaout Services pour vos besoins d'aide à domicile en Île-de-France. Formulaire en ligne, téléphone ou email. Réponse sous 24h." />
        <link rel="canonical" href="https://www.hisvie.com/contact/" />
      </Helmet>

      <div className="w-full relative overflow-hidden">
        <header className="pt-48 pb-20 px-6 max-w-7xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                <div className={`inline-flex items-center gap-2 px-5 py-2 rounded-full border mb-8 text-[11px] font-black uppercase tracking-widest shadow-sm ${isDark ? 'bg-blue-900/20 border-blue-800 text-blue-300' : 'bg-blue-50 border-blue-100 text-blue-800'}`}>
                    <FaEnvelope className="text-xs" /> À votre écoute
                </div>
                <h1 className={`text-6xl md:text-[5.5rem] font-black mb-8 tracking-tighter leading-[1.05] ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                    Parlons de <br />
                    <span className="text-blue-600 font-serif italic font-light">votre projet.</span>
                </h1>
                <div className="w-24 h-1.5 bg-blue-600 rounded-full"></div>
            </motion.div>
        </header>

        <div className="max-w-7xl mx-auto px-6 pb-32 grid lg:grid-cols-12 gap-16 items-start">
          
          <div className="lg:col-span-5 space-y-8">
            {[
              { icon: FaMapMarkerAlt, title: "Notre Siège", l1: "187 Boulevard Anatole France", l2: "93200 Saint-Denis" },
              { icon: FaPhoneAlt, title: "Contact Direct", l1: "01 43 52 64 23", l2: "his@donnerlavieauxannees.com" },
              { icon: FaClock, title: "Horaires", l1: "Lun - Ven : 9h00 - 18h00", l2: "Sam : 10h00 - 16h00" }
            ].map((info, idx) => (
              <motion.div 
                key={info.title} 
                className={`group flex items-start gap-6 p-10 rounded-[2.8rem] border transition-all duration-500 ${
                    isDark ? "bg-gray-900/50 border-gray-800 hover:border-gray-700" : "bg-white border-gray-100 shadow-xl shadow-blue-900/5"
                }`}
              >
                <div className="w-14 h-14 shrink-0 flex items-center justify-center rounded-2xl bg-blue-600 text-white shadow-lg shadow-blue-600/30">
                  <info.icon size={20} />
                </div>
                <div>
                  <h3 className={`text-lg font-black mb-2 uppercase tracking-tight ${isDark ? "text-white" : "text-[#1B365D]"}`}>{info.title}</h3>
                  <p className={`text-sm font-medium leading-relaxed ${isDark ? "text-gray-300" : "text-gray-600"}`}>{info.l1}<br/><strong className="font-bold text-blue-600">{info.l2}</strong></p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div 
            className={`lg:col-span-7 rounded-[3.5rem] shadow-2xl p-8 md:p-16 border relative overflow-hidden ${
                isDark ? "bg-gray-900 border-gray-800" : "bg-white border-gray-100 shadow-blue-900/5"
            }`}
          >
            <Box mb={16}>
                <div className="flex justify-between items-end mb-4">
                    <h2 className={`text-3xl font-black tracking-tighter ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Votre demande.</h2>
                    <span className="text-[10px] font-black uppercase tracking-widest text-blue-600">Étape {step} / 2</span>
                </div>
                <Progress value={(step / 2) * 100} height="4px" borderRadius="full" bg={isDark ? "gray.800" : "gray.100"} colorScheme="blue" />
            </Box>

            <form onSubmit={handleSubmit}>
              <AnimatePresence mode="wait">
                {step === 1 ? (
                  <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-2">
                        <label className={labelStyle}><FaUser className="mr-2 inline" /> Nom</label>
                        <input type="text" name="nom" value={formData.nom} onChange={handleChange} maxLength={60} className={`${inputStyle} ${errors.nom ? 'border-red-400' : ''}`} placeholder="Entrez votre nom" />
                        {errors.nom && <p className="text-red-500 text-xs px-2">{errors.nom}</p>}
                      </div>
                      <div className="space-y-2">
                        <label className={labelStyle}><FaUser className="mr-2 inline" /> Prénom</label>
                        <input type="text" name="prenom" value={formData.prenom} onChange={handleChange} maxLength={60} className={`${inputStyle} ${errors.prenom ? 'border-red-400' : ''}`} placeholder="Entrez votre prénom" />
                        {errors.prenom && <p className="text-red-500 text-xs px-2">{errors.prenom}</p>}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-2">
                        <label className={labelStyle}><FaEnvelope className="mr-2 inline" /> Email professionnel</label>
                        <input type="email" name="email" value={formData.email} onChange={handleChange} maxLength={100} className={`${inputStyle} ${errors.email ? 'border-red-400' : ''}`} placeholder="exemple@mail.com" />
                        {errors.email && <p className="text-red-500 text-xs px-2">{errors.email}</p>}
                      </div>
                      <div className="space-y-2">
                        <label className={labelStyle}><FaPhone className="mr-2 inline" /> Téléphone</label>
                        <input type="tel" name="telephone" value={formData.telephone} onChange={handleChange} maxLength={20} className={`${inputStyle} ${errors.telephone ? 'border-red-400' : ''}`} placeholder="06 12 34 56 78" />
                        {errors.telephone && <p className="text-red-500 text-xs px-2">{errors.telephone}</p>}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={nextStep}
                      className="w-full flex items-center justify-center gap-4 bg-blue-600 text-white font-black py-6 rounded-2xl text-[11px] uppercase tracking-widest shadow-xl shadow-blue-600/20 hover:bg-blue-700 transition-all"
                    >
                      Suivant <FaArrowRight />
                    </button>
                  </motion.div>
                ) : (
                  <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-10">
                    <div className="space-y-6">
                      <label className={labelStyle}>Sélectionnez les services</label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {serviceOptions.map((service) => (
                          <label key={service.id} className={`flex items-center justify-center p-4 rounded-2xl border-2 transition-all cursor-pointer font-black text-[9px] uppercase tracking-widest text-center ${
                            checkboxes[service.id] 
                              ? "bg-blue-600 border-blue-600 text-white" 
                              : isDark ? "bg-gray-800 border-gray-700 text-gray-400" : "bg-gray-50 border-gray-200 text-gray-500"
                          }`}>
                            <input type="checkbox" name={service.id} checked={checkboxes[service.id]} onChange={handleCheckboxChange} className="hidden" />
                            {service.label}
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className={labelStyle}>Votre message</label>
                      <textarea name="message" value={formData.message} onChange={handleChange} rows="5" maxLength={2000} className={`${inputStyle} resize-none ${errors.message ? 'border-red-400' : ''}`} placeholder="Comment pouvons-nous vous aider ?"></textarea>
                      <div className="flex justify-between px-2">
                        {errors.message ? <p className="text-red-500 text-xs">{errors.message}</p> : <span />}
                        <p className={`text-xs ${formData.message.length > 1800 ? 'text-orange-500' : 'text-gray-400'}`}>{formData.message.length}/2000</p>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4 pt-4">
                        <button type="button" onClick={prevStep} className={`flex-1 flex items-center justify-center gap-3 font-black py-6 rounded-2xl text-[11px] uppercase tracking-widest border transition-all ${isDark ? 'border-gray-700 text-white' : 'border-gray-200 text-gray-500'}`}>
                            <FaArrowLeft /> Retour
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-[2] flex items-center justify-center gap-4 bg-blue-600 hover:bg-blue-700 text-white font-black py-6 rounded-2xl text-[11px] uppercase tracking-widest shadow-xl shadow-blue-600/30 transition-all disabled:opacity-30"
                        >
                            {loading ? <Spinner size="xs" /> : <><FaPaperPlane /> Envoyer la demande</>}
                        </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </form>
          </motion.div>
        </div >
        
        <div className="max-w-7xl mx-auto px-6 mb-32">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className={`rounded-[3.5rem] shadow-2xl overflow-hidden h-[500px] border p-3 ${isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-100"}`}
          >
            <iframe
              src="https://maps.google.com/maps?q=8+place+Georges+Braque,+93120+La+Courneuve&t=&z=15&ie=UTF8&iwloc=&output=embed"
              width="100%" height="100%" style={{ border: 0, borderRadius: '2.5rem' }} allowFullScreen="" loading="lazy" title="Carte HIS"
            ></iframe>
          </motion.div>
        </div>
      </div>
    </Box>
  );
}

export default Contact;